// src/screens/admin/tickets/AdminHelp_Support.jsx

import { useState, useRef, useEffect, useContext } from "react";
import {
  Search,
  X,
  ChevronDown,
  Eye,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  MessageSquare,
  Send,
  ArrowLeft,
  Ticket,
  User,
  Tag,
  Calendar,
  Flag,
} from "lucide-react";
import "../../../assets/css/admin/adminHelp&support/AdminHelp_Support.css";
import AppContext from "../../../context/AppContext";
import axios from "axios";

// ─────────────────────────────────────────────────────────────
// CONFIG
// ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  pending: { label: "Pending", className: "at-status-open", icon: Clock },
  open: {
    label: "Open",
    className: "at-status-progress",
    icon: AlertTriangle,
  },
  closed: { label: "Closed", className: "at-status-closed", icon: XCircle },
};

const CATEGORIES = [
  "Order Issue",
  "Delivery Problem",
  "Product Question",
  "Payment Issue",
  "Return / Refund",
  "Technical Support",
  "General Inquiry",
];
const STATUSES = ["pending", "open", "closed"];

// ─────────────────────────────────────────────────────────────
// DROPDOWN
// ─────────────────────────────────────────────────────────────
const Dropdown = ({
  value,
  onChange,
  options,
  placeholder,
  labelFn,
  className: cls,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const label =
    !value || value === "all" ? placeholder : labelFn ? labelFn(value) : value;

  return (
    <div className={`at-dd-wrap ${cls || ""}`} ref={ref}>
      <button
        className="at-dd-btn"
        onClick={() => setOpen(!open)}
        type="button"
      >
        <span>{label}</span>
        <ChevronDown
          size={14}
          className={`at-dd-chevron ${open ? "at-dd-chevron-open" : ""}`}
        />
      </button>
      {open && (
        <div className="at-dd-menu">
          <button
            className={`at-dd-item ${!value || value === "all" ? "at-dd-item-active" : ""}`}
            onClick={() => {
              onChange("all");
              setOpen(false);
            }}
          >
            {placeholder}
          </button>
          {options.map((o) => (
            <button
              key={o}
              className={`at-dd-item ${value === o ? "at-dd-item-active" : ""}`}
              onClick={() => {
                onChange(o);
                setOpen(false);
              }}
            >
              {labelFn ? labelFn(o) : o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SPLIT VIEW  (ticket detail + chat)
// ─────────────────────────────────────────────────────────────
const SplitView = ({ ticket, onBack, onStatusChange }) => {
  const { baseUrl, authToken } = useContext(AppContext);
  const [chats, setChats] = useState([]);
  const [msg, setMsg] = useState("");
  const [status, setStatus] = useState(ticket.status);
  const [statusDd, setStatusDd] = useState(false);
  const chatEndRef = useRef(null);
  const statusRef = useRef(null);

  // scroll to bottom on new message
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chats]);

  // close status dropdown on outside click
  useEffect(() => {
    const h = (e) => {
      if (statusRef.current && !statusRef.current.contains(e.target))
        setStatusDd(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // to call view function
  useEffect(() => {
    viewChat();
  }, [ticket.id]);

  const viewChat = () => {
    axios
      .get(`${baseUrl}/admin/help-support/adminViewChat`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { adconversation_id: ticket.adconversation.id },
      })
      .then((response) => {
        console.log(response.data);
        setChats(response.data.chats);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const sendChat = () => {
    const formdata = new FormData();
    formdata.append("message", msg);
    formdata.append("sender", "admin");
    formdata.append("adconversation_id", ticket.adconversation.id);

    axios
      .post(`${baseUrl}/admin/help-support/sendAdminChat`, formdata, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        setMsg("");
        viewChat();
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const updateStatus = () => {
    const formData = new FormData();
    formData.append("id", ticket.id);
    formData.append("status", status);

    axios
      .post(`${baseUrl}/admin/help-support/updateTicketStatus`, formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        console.log(response.data);
        setStatus(response.data.status);
        onStatusChange(ticket.id, response.data.status);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <div className="at-split-view">
      {/* ── LEFT: Ticket Detail ── */}
      <div className="at-split-left">
        {/* back button */}
        <button className="at-back-btn" onClick={onBack}>
          <ArrowLeft size={15} /> Back to Tickets
        </button>

        {/* ticket id */}
        <div className="at-detail-head">
          <span className="at-detail-id">{ticket.id}</span>
        </div>

        {/* meta grid */}
        <div className="at-detail-meta">
          <div className="at-meta-row">
            <User size={14} className="at-meta-icon" />
            <span className="at-meta-key">Customer</span>
            <span className="at-meta-val">{ticket.adcustomer.fullName}</span>
          </div>
          <div className="at-meta-row">
            <Tag size={14} className="at-meta-icon" />
            <span className="at-meta-key">Category</span>
            <span className="at-meta-val">{ticket.category}</span>
          </div>
          <div className="at-meta-row">
            <Calendar size={14} className="at-meta-icon" />
            <span className="at-meta-key">Date</span>
            <span className="at-meta-val">
              {new Date(ticket.created_at).toLocaleDateString("en-PK", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </span>
          </div>
        </div>

        {/* customer message */}
        <div className="at-detail-message">
          <div className="at-detail-message-label">
            <MessageSquare size={13} /> Customer's Message
          </div>
          <p>{ticket.message}</p>
        </div>

        {/* status update */}
        <div className="at-detail-status-section">
          <span className="at-detail-status-label">Update Status</span>
          <div className="at-detail-status-row">
            <div className="at-dd-wrap" ref={statusRef} style={{ flex: 1 }}>
              <button
                className="at-dd-btn"
                onClick={() => setStatusDd(!statusDd)}
                type="button"
              >
                <span
                  className={`at-status-badge ${STATUS_CONFIG[status].className}`}
                >
                  {status}
                </span>
                <ChevronDown
                  size={14}
                  className={`at-dd-chevron ${statusDd ? "at-dd-chevron-open" : ""}`}
                />
              </button>
              {statusDd && (
                <div className="at-dd-menu">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      className={`at-dd-item ${status === s ? "at-dd-item-active" : ""}`}
                      onClick={() => {
                        setStatus(s);
                        setStatusDd(false);
                      }}
                    >
                      <span
                        className={`at-status-badge ${STATUS_CONFIG[s].className}`}
                      >
                        {s}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button className="at-save-btn" onClick={updateStatus}>
              <CheckCircle size={14} /> Save
            </button>
          </div>
        </div>
      </div>

      {/* ── RIGHT: Chat ── */}
      <div className="at-split-right">
        {/* chat header */}
        <div className="at-chat-header">
          <div className="at-chat-header-left">
            <div className="at-chat-avatar">
              {ticket.adcustomer.fullName.charAt(0)}
            </div>
            <div>
              <div className="at-chat-name">{ticket.adcustomer.fullName}</div>
              <div className="at-chat-sub">Live Support Chat · {ticket.id}</div>
            </div>
          </div>
          <div className="at-chat-dot-wrap">
            <span className="at-chat-dot" />{" "}
            <span className="at-chat-online">Online</span>
          </div>
        </div>

        {/* messages */}
        <div className="at-chat-body">
          {chats.length === 0 ? (
            <div className="at-chat-empty">
              <MessageSquare size={32} strokeWidth={1.3} />
              <p>
                No messages yet.
                <br />
                Start the conversation below.
              </p>
            </div>
          ) : (
            chats.map((m, i) => (
              <div
                key={i}
                className={`at-chat-msg ${m.sender === "admin" ? "at-chat-msg-admin" : "at-chat-msg-customer"}`}
              >
                <div className="at-chat-bubble">{m.message}</div>
                <span className="at-chat-time">
                  {m.sender === "admin" ? "You" : ticket.adcustomer.fullName} ·{" "}
                  {m.time}
                </span>
              </div>
            ))
          )}
          <div ref={chatEndRef} />
        </div>

        {/* input */}
        <div className="at-chat-input-row">
          <input
            className="at-chat-input"
            placeholder="Type a reply..."
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendChat()}
          />
          <button className="at-chat-send" onClick={sendChat} aria-label="Send">
            <Send size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const AdminHelp_Support = () => {
  const { baseUrl, authToken } = useContext(AppContext);
  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCat, setFilterCat] = useState("all");
  const [filterPri, setFilterPri] = useState("all");
  const [activeTicket, setActiveTicket] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    viewAllTickets();
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const viewAllTickets = () => {
    axios
      .get(`${baseUrl}/admin/help-support/viewAllTickets`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        setTickets(response.data.tickets);
        console.log(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleStatusChange = (id, newStatus) => {
    setTickets((prev) =>
      prev.map((t) => (t.id === id ? { ...t, status: newStatus } : t)),
    );
    // also update activeTicket so split view reflects change
    setActiveTicket((prev) => (prev ? { ...prev, status: newStatus } : prev));
    setToast({ msg: `Ticket ${id} updated to "${newStatus}"` });
  };

  // ── Filter ──
  const filtered = tickets.filter((t) => {
    const q = search.toLowerCase();
    return (
      (t.adcustomer?.fullName.toLowerCase().includes(q) ||
        String(t.id).toLowerCase().includes(q)) &&
      (filterStatus === "all" || t.status === filterStatus) &&
      (filterCat === "all" || t.category === filterCat)
    );
  });

  // stats
  const openCount = tickets.filter((t) => t.status === "open").length;

  // ── SPLIT VIEW active ──
  if (activeTicket) {
    return (
      <div className="at-container">
        {toast && (
          <div className="at-toast at-toast-success">
            <CheckCircle size={13} /> {toast.msg}
          </div>
        )}
        <SplitView
          ticket={activeTicket}
          onBack={() => setActiveTicket(null)}
          onStatusChange={handleStatusChange}
        />
      </div>
    );
  }

  // ── LIST VIEW ──
  return (
    <div className="at-container">
      {toast && (
        <div className="at-toast at-toast-success">
          <CheckCircle size={13} /> {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="at-header">
        <div>
          <h1 className="at-title">Support Tickets</h1>
          <p className="at-subtitle">
            Click any ticket to view details and chat with the customer
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="at-stats-row">
        <div className="at-stat-card">
          <span className="at-stat-num">{tickets.length}</span>
          <span className="at-stat-label">Total</span>
        </div>
        <div className="at-stat-card at-stat-open">
          <span className="at-stat-num">{openCount}</span>
          <span className="at-stat-label">Open</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="at-toolbar">
        <div className="at-search-wrap">
          <Search size={14} className="at-search-icon" />
          <input
            className="at-search-input"
            placeholder="Search by ID, customer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="at-search-clear" onClick={() => setSearch("")}>
              <X size={13} />
            </button>
          )}
        </div>
        <Dropdown
          value={filterStatus}
          onChange={setFilterStatus}
          options={STATUSES}
          placeholder="All Statuses"
        />
        <Dropdown
          value={filterCat}
          onChange={setFilterCat}
          options={CATEGORIES}
          placeholder="All Categories"
        />
      </div>

      <p className="at-result-info">
        Showing <strong>{filtered.length}</strong> of{" "}
        <strong>{tickets.length}</strong> tickets
      </p>

      {/* Table */}
      <div className="at-table-container">
        {filtered.length === 0 ? (
          <div className="at-empty">
            <Ticket size={40} strokeWidth={1.3} className="at-empty-icon" />
            <h3>No tickets found</h3>
            <p>Try adjusting your search or filters</p>
            {(search ||
              filterStatus !== "all" ||
              filterCat !== "all" ||
              filterPri !== "all") && (
              <button
                className="at-save-btn"
                style={{ marginTop: "1rem" }}
                onClick={() => {
                  setSearch("");
                  setFilterStatus("all");
                  setFilterCat("all");
                  setFilterPri("all");
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <table className="at-table">
            <thead>
              <tr>
                <th>Ticket ID</th>
                <th>Customer</th>
                <th>Category</th>
                <th>Status</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const { className: sClass, label: sLabel } =
                  STATUS_CONFIG[t.status];
                return (
                  <tr
                    key={t.id}
                    className="at-table-row-clickable"
                    onClick={() => setActiveTicket(t)}
                  >
                    <td className="at-td-id">{t.id}</td>
                    <td className="at-td-customer">{t.adcustomer?.fullName}</td>
                    <td>
                      <span className="at-cat-badge">{t.category}</span>
                    </td>
                    <td>
                      <span className={`at-status-badge ${sClass}`}>
                        {sLabel}
                      </span>
                    </td>
                    <td className="at-td-date">
                      {new Date(t.created_at).toLocaleDateString("en-PK", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>
                    <td>
                      <button
                        className="at-action-btn at-action-view"
                        title="View & Chat"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTicket(t);
                        }}
                      >
                        <Eye size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminHelp_Support;
